import { Tag } from '@ads/components-react';
import { ReceiptPercentIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentEditorViewModel } from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';

import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { formatDateTime } from '../../../utils/formatDateTime';
import { PlanReadOnlyField } from '../../molecules/PlanReadOnlyField';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';

/** Props de `PlanContractCard`. */
export interface PlanContractCardProps {
  /** Resposta do editor; `undefined` enquanto carrega. */
  readonly editor: IndividualPaymentEditorViewModel | undefined;
  /** Exibe esqueletos em vez dos campos. */
  readonly isLoading?: boolean | undefined;
  /**
   * Ações de leitura do contrato, exibidas no cabeçalho do card.
   *
   * Ficam aqui e não na régua da página porque as três — rastreamento de
   * pedidos, rastreamento de operações e visualizar parcelas — são navegações
   * sobre **este contrato**, não sobre a aba aberta.
   */
  readonly actions?: ReactNode | undefined;
}

/** Linha de três esqueletos, na grade do card. */
function SkeletonRow({ id }: { readonly id: string }) {
  return (
    <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
      {['a', 'b', 'c'].map(cell => (
        <div key={`${id}-${cell}`} className="flex flex-col gap-quark">
          <Skeleton className="h-mini w-[6rem] rounded-sm" />
          <Skeleton className="h-xs w-[11rem] rounded-sm" />
        </div>
      ))}
    </div>
  );
}

/**
 * Card somente-leitura do contrato financeiro, visível nas três abas.
 *
 * `status` é apresentado como **status do plano**, em campo próprio — e não como
 * indicador ao lado do nome do aluno, que o leria como "aluno ativo".
 *
 * `type` e `status` chegam como `number` sem texto correspondente e sem endpoint
 * de lookup. São exibidos como o código recebido, na mesma convenção do
 * `ContractHeaderCard` da tela de parcelas: um mapa adivinhado no frontend
 * exibiria o rótulo errado com confiança quando o backend criasse um id novo.
 */
export function PlanContractCard({ editor, isLoading, actions }: PlanContractCardProps) {
  const { t } = useTranslation('individualPayment');

  const rawCode = (value: number | null | undefined) =>
    value === null || value === undefined ? EMPTY_VALUE : t('planEdit.contract.rawCode', { value });

  /**
   * Rótulo traduzido do enum, com o código bruto como recuo.
   *
   * O editor devolve o par id + texto (`type`/`typeText`, `status`/`statusText`)
   * já traduzido na cultura da request — é ele que deve aparecer. O "Código N" só
   * entra quando o texto vem vazio, porque um id ainda diz mais que um traço.
   *
   * **`enrollmentStatus` fica de fora**: é o único dos três sem irmão textual na
   * resposta, e nenhum lookup da API devolve esses rótulos. Segue em `rawCode`
   * até o backend expor o texto.
   */
  const label = (text: string | null | undefined, value: number | null | undefined) =>
    text !== null && text !== undefined && text.trim() !== '' ? text : rawCode(value);

  const closedParcels =
    editor?.closedParcels !== undefined && editor?.numberParcels !== undefined
      ? t('planEdit.contract.closedOf', {
          closed: editor.closedParcels,
          total: editor.numberParcels,
        })
      : EMPTY_VALUE;

  return (
    <PlanSectionCard
      title={t('planEdit.contract.title')}
      icon={ReceiptPercentIcon}
      // A badge "Somente leitura" sai quando há ações: ela se referia aos campos,
      // mas ao lado de três botões passaria a ler como se o card todo fosse
      // inerte. Os campos já se distinguem visualmente de inputs.
      {...(actions ? {} : { badge: t('planEdit.contract.readOnly') })}
      headerSlot={actions}
    >
      {isLoading ? (
        <>
          <SkeletonRow id="row-1" />
          <SkeletonRow id="row-2" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
            <PlanReadOnlyField
              label={t('planEdit.contract.student')}
              value={editor?.student ?? EMPTY_VALUE}
              hint={editor?.email}
            />
            <PlanReadOnlyField
              label={t('planEdit.contract.course')}
              value={editor?.courseName ?? EMPTY_VALUE}
            />
            <PlanReadOnlyField
              label={t('planEdit.contract.type')}
              value={label(editor?.typeText, editor?.type)}
            />
          </div>

          <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
            <PlanReadOnlyField label={t('planEdit.contract.status')}>
              <span className="flex">
                <Tag
                  label={label(editor?.statusText, editor?.status)}
                  size="sm"
                  status="default"
                  custom={{ text: 'text-nowrap' }}
                />
              </span>
            </PlanReadOnlyField>
            <PlanReadOnlyField label={t('planEdit.contract.enrollmentStatus')}>
              <span className="flex">
                <Tag
                  label={rawCode(editor?.enrollmentStatus)}
                  size="sm"
                  status="highlight"
                  custom={{ text: 'text-nowrap' }}
                />
              </span>
            </PlanReadOnlyField>
            <PlanReadOnlyField label={t('planEdit.contract.closedParcels')} value={closedParcels} />
          </div>

          <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
            <PlanReadOnlyField
              label={t('planEdit.contract.integrationDate')}
              value={formatDateTime(editor?.customerIntegrationDate)}
            />
          </div>
        </>
      )}
    </PlanSectionCard>
  );
}
